import { useParams, useHistory } from 'react-router-dom';
import { useCallback, useState } from 'react';
import { addToast } from '@octano/global-ui';
import { useTranslation } from 'react-i18next';

import { AxiosResult, AxiosResultDefaultError } from '../../../api/request';

import CourseProvider from './providers/CourseProvider';
import CourseForm from './parts/CourseForm';
import { PathsLayouts } from '../../../config/routes';
import { CourseFormFields } from './types';
import {
  removeCourseSyllabus,
  requestCreateCourse,
  requestUpdateCourseAdministrative,
  requestUpdateCourseTeaching,
  updateCourseSyllabus,
} from './api';
import {
  creationPayload,
  updatingAdministrativePayload,
  updatingTeachingPayload,
} from './utils';
import { TranslationsKeys } from '../../../locales/translations';

const CourseUpdate = () => {
  const { t } = useTranslation(TranslationsKeys.ACADEMIC_OFFER_COURSES_UPDATE);
  const { id: courseId } = useParams<{ id: string }>();
  const history = useHistory();

  const [processing, setProcessing] = useState<boolean>(false);

  const handleBack = useCallback(() => {
    history.push(`${PathsLayouts.academicOffers}/curricular-subjects`);
  }, [history]);

  const responseCreateOrUpdate = useCallback(
    (error: false | AxiosResultDefaultError | undefined) => {
      if (error) {
        const messages = error?.data?.message
          ? [error.data.message].flat()
          : [];
        if (messages.some((err: string) => err === 'SHORT_IN_USE')) {
          addToast({
            icon: 'warning',
            color: 'warning',
            text: t(`validateShortening`),
          });
        } else if (messages.some((err: string) => err === 'COD_IN_USE')) {
          addToast({
            icon: 'warning',
            color: 'warning',
            text: t(`validateCode`),
          });
        } else {
          addToast({
            icon: 'error',
            color: 'danger',
            text: t('common:errors.save'),
          });
        }
      } else {
        addToast({
          icon: 'success',
          color: 'success',
          text: t(
            `common:messages.${!courseId ? 'saveSuccess' : 'editSuccess'}`,
          ),
        });
        handleBack();
      }
    },
    [handleBack, t, courseId],
  );

  const onUpdateSyllabus = useCallback(
    async (id: string, values: CourseFormFields) => {
      if (values?.syllabusFile) {
        if (values?.syllabusFile?.lastModified !== -2) {
          await updateCourseSyllabus({
            id: +id,
            file: values?.syllabusFile,
          });
        }
      } else {
        await removeCourseSyllabus(+id);
      }
    },
    [],
  );

  // Submit del tab Administrativo:
  //   - CREATE: un único POST, y luego sube syllabus con el id devuelto.
  //   - UPDATE: PATCH /administrative.
  const handleSubmitAdmin = useCallback(
    async ({
      values,
      isDisabledToUpdate,
    }: {
      values: CourseFormFields;
      isDisabledToUpdate: boolean;
    }) => {
      if (processing) {
        return;
      }
      setProcessing(true);

      try {
        if (!courseId) {
          const { error, data } = await requestCreateCourse(
            creationPayload(values),
          );
          if (!error && data?.id) {
            await onUpdateSyllabus(`${data.id}`, values);
          }
          responseCreateOrUpdate(error);
          return;
        }

        const { error } = await requestUpdateCourseAdministrative(
          +courseId,
          updatingAdministrativePayload(+courseId, values, isDisabledToUpdate),
        );
        responseCreateOrUpdate(error);
      } finally {
        setProcessing(false);
      }
    },
    [processing, courseId, responseCreateOrUpdate, onUpdateSyllabus],
  );

  // Submit del tab Docencia:
  //   - Solo disponible en UPDATE/WATCH (la pestaña no se renderiza en CREATE).
  //   - Lanza PATCH /teaching y actualiza/borra syllabus en paralelo.
  const handleSubmitTeaching = useCallback(
    async ({ values }: { values: CourseFormFields }) => {
      if (processing || !courseId) {
        return;
      }
      setProcessing(true);

      try {
        const requests: Promise<AxiosResult<any>>[] = [
          requestUpdateCourseTeaching(
            +courseId,
            updatingTeachingPayload(+courseId, values),
          ),
          onUpdateSyllabus(courseId, values).then(
            () => ({ data: undefined as any } as AxiosResult<any>),
          ),
        ];

        const results = await Promise.all(requests);
        const firstError = results.find((r) => r?.error)?.error;
        responseCreateOrUpdate(firstError);
      } finally {
        setProcessing(false);
      }
    },
    [processing, courseId, responseCreateOrUpdate, onUpdateSyllabus],
  );

  return (
    <CourseProvider courseId={courseId}>
      <CourseForm
        processing={processing}
        onBack={handleBack}
        onSubmitAdmin={handleSubmitAdmin}
        onSubmitTeaching={handleSubmitTeaching}
      />
    </CourseProvider>
  );
};

export default CourseUpdate;
